import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { WindowRef } from '@boels/helpers';

@Component({
  selector: 'boels-skeleton',
  templateUrl: './boels-skeleton.component.html',
  styleUrls: ['./boels-skeleton.component.scss'],
  imports: [CommonModule],
  animations: [
    trigger('fade', [
      state('invisible', style({ opacity: 0 })),
      state('visible', style({ opacity: 1 })),
      transition('invisible => visible', animate('200ms ease-in')),
      transition('visible => invisible', animate('200ms ease-out')),
    ]),
  ],
})
export class BoelsSkeletonComponent implements OnChanges, OnInit, OnDestroy {
  private winRef = inject(WindowRef);

  @Input() public runOnServer: boolean;
  /**
   * @property inputWidth - The width of the skeleton element.
   */
  @Input() public inputWidth: number;

  /**
   * @property inputHeight - The height of the skeleton element.
   */
  @Input() public inputHeight: number;

  /**
   * @property inputRoundShape - If true, the skeleton element will be a circle of 50px diameter. The diameter can be changed by setting the inputWidth and inputHeight properties.
   */
  @Input() public inputRoundShape: boolean;

  /**
   * @property inputStyle - The styles to be applied to each of the skeleton elements.
   */
  @Input() public inputStyle: Record<string, string | number> = {};

  /**
   * Adds the styles to each of the skeleton element.
   */
  public _inputStyle: Record<string, string | number> = {};
  /**
   * Render control
   */
  @Input() public delayRender = 500;
  @Input() public delayDestroy = 1000;
  public visibilityState: 'visible' | 'invisible' = 'invisible';

  public ngOnChanges(): void {
    if (this.winRef.isBrowser() || this.runOnServer) {
      const width = this.inputWidth ? `${this.inputWidth}px` : '100%';

      this._inputStyle = {
        height: this.inputHeight ? `${this.inputHeight}px` : 'auto',
        width: width,
        borderRadius: this.inputRoundShape ? width : '0',
        ...this.inputStyle,
      };
    }
  }

  public ngOnInit(): void {
    if (this.winRef.isBrowser() || this.runOnServer) {
      setTimeout(() => {
        this.visibilityState = 'visible';
      }, this.delayRender);
    }
  }

  public ngOnDestroy(): void {
    if (this.winRef.isBrowser() || this.runOnServer) {
      if (this.visibilityState === 'visible') {
        setTimeout(() => {
          this.visibilityState = 'invisible';
        }, this.delayDestroy);
      }
    }
  }
}
